import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    History, Search, Plus, Pencil, Trash2, Ban, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { mensajeDeError } from '../../utils/errores';

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

const ACCIONES = {
    alta:         { etiqueta: 'Alta',         icono: <Plus size={13} />,   clase: 'bg-exito-500/10 text-exito-700 dark:text-exito-400 border-exito-500/20' },
    modificacion: { etiqueta: 'Modificación', icono: <Pencil size={13} />, clase: 'bg-brand-500/10 text-brand-800 dark:text-brand-400 border-brand-500/20' },
    baja:         { etiqueta: 'Baja',         icono: <Trash2 size={13} />, clase: 'bg-error-500/10 text-error-700 dark:text-error-400 border-error-500/20' },
    anulacion:    { etiqueta: 'Anulación',    icono: <Ban size={13} />,    clase: 'bg-alerta-500/10 text-alerta-700 dark:text-alerta-400 border-alerta-500/20' },
};

const ENTIDADES = [
    { valor: '', etiqueta: 'Todo' },
    { valor: 'producto', etiqueta: 'Artículos' },
    { valor: 'cliente', etiqueta: 'Clientes' },
    { valor: 'venta', etiqueta: 'Ventas' },
];

const fecha = iso => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Lo que cambió, en una línea que se entienda sin abrir nada. */
function Cambios({ cambios }) {
    const campos = Object.keys(cambios || {});
    if (!campos.length) return null;
    return (
        <div className="mt-1 space-y-0.5">
            {campos.map(campo => (
                <p key={campo} className="text-xs text-slate-500 dark:text-slate-400 break-words">
                    <span className="font-semibold">{campo.replace(/_/g, ' ')}:</span>{' '}
                    <span className="line-through opacity-70">{cambios[campo].antes}</span>
                    {' → '}
                    <span className="font-semibold">{cambios[campo].despues}</span>
                </p>
            ))}
        </div>
    );
}

function Etiqueta({ accion }) {
    const meta = ACCIONES[accion] || { etiqueta: accion, icono: null, clase: 'bg-slate-500/10 text-slate-600 border-slate-500/20' };
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${meta.clase}`}>
            {meta.icono}{meta.etiqueta}
        </span>
    );
}

/**
 * El registro de operaciones del negocio.
 *
 * Se arma en dos presentaciones y no en una tabla con desplazamiento lateral:
 * en el celular, una tarjeta por operación; de tablet en adelante, la tabla.
 * Una tabla de siete columnas en un teléfono obliga a arrastrar para leer cada
 * fila, y este registro justamente se consulta con apuro, cuando los números
 * no cierran.
 */
export default function RegistroOperaciones() {
    const [operaciones, setOperaciones] = useState([]);
    const [entidad, setEntidad] = useState('');
    const [usuario, setUsuario] = useState('');
    const [pagina, setPagina] = useState(1);
    const [total, setTotal] = useState(0);
    const [hayMas, setHayMas] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        try {
            const params = { page: pagina };
            if (entidad) params.entidad = entidad;
            if (usuario.trim()) params.usuario = usuario.trim();
            const { data } = await axios.get(`${BASE}/operaciones/`, { params });
            setOperaciones(data.results || []);
            setTotal(data.count || 0);
            setHayMas(Boolean(data.next));
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo cargar el registro de operaciones.'));
            setOperaciones([]);
        } finally {
            setCargando(false);
        }
    }, [pagina, entidad, usuario]);

    useEffect(() => { cargar(); }, [cargar]);

    // Cambiar un filtro vuelve a la primera página: si no, se filtra y queda
    // una página vacía porque el resultado nuevo tiene menos registros.
    const filtrarPor = valor => { setEntidad(valor); setPagina(1); };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <History className="text-brand-800 dark:text-brand-400" size={26} /> Registro de operaciones
                </h1>
                <p className="ayuda-campo">
                    Quién dio de alta, modificó, dio de baja o anuló, y qué cambió exactamente.
                    No se puede editar ni borrar.
                </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {ENTIDADES.map(e => (
                        <button
                            key={e.valor || 'todo'}
                            type="button"
                            onClick={() => filtrarPor(e.valor)}
                            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${
                                entidad === e.valor
                                    ? 'bg-brand-500 text-negro-950'
                                    : 'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-300'}`}
                        >
                            {e.etiqueta}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 min-w-0">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        aria-label="Filtrar por usuario"
                        placeholder="Filtrar por usuario"
                        value={usuario}
                        onChange={e => { setUsuario(e.target.value); setPagina(1); }}
                        className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium text-sm"
                    />
                </div>
            </div>

            {error && (
                <p className="p-4 rounded-2xl bg-error-50 dark:bg-error-950/40 text-sm font-semibold text-error-700 dark:text-error-400">
                    {error}
                </p>
            )}

            {cargando && <p className="ayuda-campo">Cargando…</p>}

            {!cargando && !error && operaciones.length === 0 && (
                <p className="p-6 rounded-2xl bg-slate-100 dark:bg-white/5 text-sm text-slate-500 dark:text-slate-400">
                    Todavía no hay operaciones registradas{entidad ? ' para ese filtro' : ''}.
                </p>
            )}

            {/* En el celular: una tarjeta por operación. */}
            {!cargando && operaciones.length > 0 && (
                <div className="md:hidden space-y-3">
                    {operaciones.map(o => (
                        <div key={o.id} className="p-4 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10">
                            <div className="flex items-start justify-between gap-2">
                                <Etiqueta accion={o.accion} />
                                <span className="text-xs text-slate-400 whitespace-nowrap">{fecha(o.fecha)}</span>
                            </div>
                            <p className="mt-2 font-bold text-slate-800 dark:text-white break-words">{o.descripcion}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {o.entidad} · {o.usuario}{o.rol ? ` (${o.rol})` : ''}
                            </p>
                            <Cambios cambios={o.cambios} />
                            {o.motivo && (
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 break-words">
                                    <span className="font-semibold">Motivo:</span> {o.motivo}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* De tablet en adelante: la tabla. */}
            {!cargando && operaciones.length > 0 && (
                <div className="hidden md:block rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="tabla-cabecera">
                                <th className="px-5 py-3 font-medium">Fecha</th>
                                <th className="px-5 py-3 font-medium">Acción</th>
                                <th className="px-5 py-3 font-medium">Qué</th>
                                <th className="px-5 py-3 font-medium">Quién</th>
                                <th className="px-5 py-3 font-medium">Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operaciones.map(o => (
                                <tr key={o.id} className="border-t border-slate-100 dark:border-white/5">
                                    <td className="px-5 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{fecha(o.fecha)}</td>
                                    <td className="px-5 py-3"><Etiqueta accion={o.accion} /></td>
                                    <td className="px-5 py-3">
                                        <p className="font-semibold text-slate-800 dark:text-white">{o.descripcion}</p>
                                        <p className="text-xs text-slate-400">{o.entidad}</p>
                                    </td>
                                    <td className="px-5 py-3 whitespace-nowrap">
                                        <p className="font-medium text-slate-700 dark:text-slate-200">{o.usuario}</p>
                                        <p className="text-xs text-slate-400">{o.rol}</p>
                                    </td>
                                    <td className="px-5 py-3">
                                        <Cambios cambios={o.cambios} />
                                        {o.motivo && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                <span className="font-semibold">Motivo:</span> {o.motivo}
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(pagina > 1 || hayMas) && (
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        disabled={pagina === 1}
                        onClick={() => setPagina(p => p - 1)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-200 font-bold text-sm disabled:opacity-40"
                    >
                        <ChevronLeft size={16} /> Anterior
                    </button>
                    <span className="text-xs text-slate-400">{total} en total</span>
                    <button
                        type="button"
                        disabled={!hayMas}
                        onClick={() => setPagina(p => p + 1)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-200 font-bold text-sm disabled:opacity-40"
                    >
                        Siguiente <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
